import {
	CodeBlock,
	Endpoint,
	Alert,
	Badge,
	Table,
	ParamList,
	Param,
	Step,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function GamesPlinko() {
	const lowRiskMultipliers = [
		{
			rows: 8,
			multipliers: '5.6x, 2.1x, 1.1x, 1.0x, 0.5x, 1.0x, 1.1x, 2.1x, 5.6x',
		},
		{
			rows: 10,
			multipliers: '8.9x, 3.0x, 1.4x, 1.1x, 1.0x, 0.5x, 1.0x, 1.1x, 1.4x, 3.0x, 8.9x',
		},
		{
			rows: 12,
			multipliers:
				'10x, 3.0x, 1.6x, 1.4x, 1.1x, 1.0x, 0.5x, 1.0x, 1.1x, 1.4x, 1.6x, 3.0x, 10x',
		},
		{
			rows: 14,
			multipliers:
				'16x, 4.0x, 2.0x, 1.4x, 1.2x, 1.1x, 1.0x, 0.5x, 1.0x, 1.1x, 1.2x, 1.4x, 2.0x, 4.0x, 16x',
		},
		{
			rows: 16,
			multipliers:
				'24x, 6.2x, 3.0x, 1.8x, 1.4x, 1.2x, 1.1x, 1.0x, 0.5x, 1.0x, 1.1x, 1.2x, 1.4x, 1.8x, 3.0x, 6.2x, 24x',
		},
	];

	const mediumRiskMultipliers = [
		{
			rows: 8,
			multipliers: '13x, 3.0x, 1.3x, 0.7x, 0.4x, 0.7x, 1.3x, 3.0x, 13x',
		},
		{
			rows: 10,
			multipliers: '22x, 5.0x, 2.0x, 1.4x, 0.6x, 0.4x, 0.6x, 1.4x, 2.0x, 5.0x, 22x',
		},
		{
			rows: 12,
			multipliers:
				'33x, 11x, 4.0x, 2.0x, 1.1x, 0.6x, 0.3x, 0.6x, 1.1x, 2.0x, 4.0x, 11x, 33x',
		},
		{
			rows: 14,
			multipliers:
				'58x, 15x, 5.0x, 3.0x, 1.5x, 1.0x, 0.5x, 0.3x, 0.5x, 1.0x, 1.5x, 3.0x, 5.0x, 15x, 58x',
		},
		{
			rows: 16,
			multipliers:
				'110x, 41x, 10x, 5.0x, 3.0x, 1.5x, 1.0x, 0.5x, 0.3x, 0.5x, 1.0x, 1.5x, 3.0x, 5.0x, 10x, 41x, 110x',
		},
	];

	const highRiskMultipliers = [
		{
			rows: 8,
			multipliers: '29x, 4.0x, 1.5x, 0.3x, 0.2x, 0.3x, 1.5x, 4.0x, 29x',
		},
		{
			rows: 10,
			multipliers: '76x, 10x, 3.0x, 0.9x, 0.3x, 0.2x, 0.3x, 0.9x, 3.0x, 10x, 76x',
		},
		{
			rows: 12,
			multipliers:
				'170x, 24x, 8.1x, 2.0x, 0.7x, 0.2x, 0.2x, 0.2x, 0.7x, 2.0x, 8.1x, 24x, 170x',
		},
		{
			rows: 14,
			multipliers:
				'420x, 56x, 18x, 5.0x, 1.9x, 0.3x, 0.2x, 0.2x, 0.2x, 0.3x, 1.9x, 5.0x, 18x, 56x, 420x',
		},
		{
			rows: 16,
			multipliers:
				'1000x, 130x, 26x, 9.0x, 4.0x, 2.0x, 0.2x, 0.2x, 0.2x, 0.2x, 0.2x, 2.0x, 4.0x, 9.0x, 26x, 130x, 1000x',
		},
	];

	const multiplierColumns = [
		{ key: 'rows', header: 'Rows' },
		{ key: 'multipliers', header: 'Multipliers (left to right)' },
	];

	return (
		<div className={styles.docsPage}>
			<h1>Plinko Game</h1>
			<p className={styles.lead}>
				A visually engaging game where a ball drops through a pegged pyramid
				board, bouncing left or right at each level until landing in a
				multiplier bucket. Configure the number of rows and risk level to
				customize payouts.
			</p>

			<Alert variant="info" title="House Edge">
				The Plinko game has a fixed 1% house edge built into the multiplier
				tables. The expected return is 99% of the bet amount over time.
			</Alert>

			<h2>Game Overview</h2>
			<p>
				Plinko simulates a physical ball dropping through a pyramid of pegs. At
				each peg, the ball has a 50% chance of bouncing left or right. The final
				landing position determines the payout multiplier.
			</p>

			<h3>Key Features</h3>
			<ul>
				<li>
					<strong>Configurable rows:</strong> Choose between 8, 10, 12, 14, or
					16 rows
				</li>
				<li>
					<strong>Risk levels:</strong> Low, Medium, or High risk multiplier
					distributions
				</li>
				<li>
					<strong>Visual path:</strong> The API returns the complete ball path
					for animation
				</li>
				<li>
					<strong>Symmetric payouts:</strong> Multipliers are symmetrical
					(highest at edges, lowest in center)
				</li>
			</ul>

			<h3>How It Works</h3>
			<ol>
				<li>Player selects the number of rows (8, 10, 12, 14, or 16)</li>
				<li>Player chooses a risk level (low, medium, or high)</li>
				<li>Player places their bet amount</li>
				<li>
					The server generates a provably fair path through the pegs (N
					left/right decisions)
				</li>
				<li>
					The ball's final position determines the multiplier bucket and payout
				</li>
			</ol>

			<h2>Bet Parameters</h2>
			<ParamList>
				<Param name="amount" type="number" required>
					The bet amount in the player's currency. Must be positive and within
					the operator's configured min/max limits.
				</Param>
				<Param name="rows" type="number" required>
					The number of rows in the Plinko board. Valid values are{' '}
					<code>8</code>, <code>10</code>, <code>12</code>, <code>14</code>, or{' '}
					<code>16</code>. More rows means more possible outcomes and landing
					positions.
				</Param>
				<Param name="risk" type="string" required>
					The risk level which determines the multiplier distribution. Valid
					values are <code>"low"</code>, <code>"medium"</code>, or{' '}
					<code>"high"</code>. Higher risk means more volatile payouts.
				</Param>
				<Param name="currency" type="string">
					Optional currency code (e.g., "USD", "BTC"). Defaults to the session's
					primary currency.
				</Param>
			</ParamList>

			<h2>Multiplier Tables</h2>
			<p>
				Multipliers vary based on the number of rows and risk level. The values
				shown below go from left edge to right edge of the board.
			</p>

			<h3>
				Low Risk <Badge variant="optional">Conservative</Badge>
			</h3>
			<p>
				Smaller range of multipliers with more frequent medium wins and rare
				losses.
			</p>
			<Table columns={multiplierColumns} data={lowRiskMultipliers} />

			<h3>
				Medium Risk <Badge variant="optional">Balanced</Badge>
			</h3>
			<p>Moderate variance with a mix of small wins and larger edge payouts.</p>
			<Table columns={multiplierColumns} data={mediumRiskMultipliers} />

			<h3>
				High Risk <Badge variant="new">Volatile</Badge>
			</h3>
			<p>
				Maximum variance with potential for massive edge multipliers but
				frequent small payouts.
			</p>
			<Table columns={multiplierColumns} data={highRiskMultipliers} />

			<Alert variant="warning" title="Risk Consideration">
				High risk with 16 rows can pay up to <strong>1000x</strong> but also has
				very low center multipliers (0.2x). Choose your risk level based on your
				bankroll and risk tolerance.
			</Alert>

			<h2>Path Generation</h2>
			<p>
				The ball's path through the board is determined by a series of binary
				decisions (left or right) at each peg level. For N rows, there are N
				decisions, and the final position is one of N+1 possible buckets.
			</p>

			<CodeBlock language="typescript" title="Path Generation Algorithm">
				{`// Each decision is derived from the provably fair hash
function generatePlinkoPath(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  rows: number
): { path: ('L' | 'R')[]; position: number } {
  const hash = createHmac('sha256', serverSeed)
    .update(\`\${clientSeed}:\${nonce}\`)
    .digest('hex');

  const path: ('L' | 'R')[] = [];
  let position = 0;

  for (let i = 0; i < rows; i++) {
    // Use 2 hex characters (1 byte) for each decision
    const byteIndex = i * 2;
    const byte = parseInt(hash.substring(byteIndex, byteIndex + 2), 16);

    // Even = Left (0), Odd = Right (1)
    if (byte % 2 === 1) {
      path.push('R');
      position++;
    } else {
      path.push('L');
    }
  }

  return { path, position };
}

// Example: 8 rows
// Path: ['L', 'R', 'R', 'L', 'R', 'L', 'L', 'R']
// Position: 4 (center bucket)`}
			</CodeBlock>

			<h3>Understanding Position</h3>
			<p>
				The final position is simply the count of right bounces. With N rows:
			</p>
			<ul>
				<li>Position 0 = leftmost bucket (0 right bounces)</li>
				<li>Position N = rightmost bucket (N right bounces)</li>
				<li>
					Position N/2 = center bucket (equal left and right bounces - most
					common)
				</li>
			</ul>

			<h2>API Endpoints</h2>

			<h3>Place Plinko Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/games/plinko/bet"
				description="Place a new Plinko bet and receive the result with ball path"
			/>

			<h4>Request Body</h4>
			<CodeBlock language="json" title="Request">
				{`{
  "sessionToken": "eyJhbGciOiJIUzI1NiIs...",
  "amount": 5.00,
  "rows": 12,
  "risk": "medium",
  "currency": "USD",
  "clientSeed": "plinko-seed-456"
}`}
			</CodeBlock>

			<h4>Response</h4>
			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "betId": "bet_plinko_def456ghi",
    "game": "plinko",
    "amount": 5.00,
    "currency": "USD",
    "rows": 12,
    "risk": "medium",
    "path": ["L", "R", "L", "L", "R", "R", "L", "R", "L", "L", "R", "L"],
    "position": 5,
    "multiplier": 0.6,
    "win": false,
    "payout": 3.00,
    "profit": -2.00,
    "balanceAfter": 98.00,
    "timestamp": "2024-01-15T14:22:30.456Z",
    "provablyFair": {
      "serverSeedHash": "b2c3d4e5f6a7...",
      "clientSeed": "plinko-seed-456",
      "nonce": 15,
      "serverSeed": null
    }
  }
}`}
			</CodeBlock>

			<h3>Get Plinko Game Info</h3>
			<Endpoint
				method="GET"
				path="/api/v1/games/plinko/info"
				description="Get game configuration, multiplier tables, and limits"
			/>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "game": "plinko",
    "name": "Plinko",
    "description": "Ball drop through pegged pyramid board",
    "houseEdge": 1.0,
    "minBet": 0.10,
    "maxBet": 5000.00,
    "rowOptions": [8, 10, 12, 14, 16],
    "riskLevels": ["low", "medium", "high"],
    "multipliers": {
      "8": {
        "low": [5.6, 2.1, 1.1, 1.0, 0.5, 1.0, 1.1, 2.1, 5.6],
        "medium": [13, 3.0, 1.3, 0.7, 0.4, 0.7, 1.3, 3.0, 13],
        "high": [29, 4.0, 1.5, 0.3, 0.2, 0.3, 1.5, 4.0, 29]
      }
    }
  }
}`}
			</CodeBlock>

			<h2>Complete Integration Example</h2>
			<p>
				Here is a complete TypeScript example showing how to integrate the
				Plinko game with path animation support:
			</p>

			<CodeBlock language="typescript" title="plinko-integration.ts" showLineNumbers>
				{`interface PlinkoBetParams {
  sessionToken: string;
  amount: number;
  rows: 8 | 10 | 12 | 14 | 16;
  risk: 'low' | 'medium' | 'high';
  clientSeed?: string;
}

interface PlinkoBetResult {
  betId: string;
  path: ('L' | 'R')[];
  position: number;
  multiplier: number;
  win: boolean;
  payout: number;
  provablyFair: {
    serverSeedHash: string;
    clientSeed: string;
    nonce: number;
  };
}

interface PlinkoMultipliers {
  [rows: number]: {
    low: number[];
    medium: number[];
    high: number[];
  };
}

const API_BASE = 'https://api.ringfort.studio/v1';

// Multiplier tables for reference (abbreviated)
const MULTIPLIERS: PlinkoMultipliers = {
  8: {
    low: [5.6, 2.1, 1.1, 1.0, 0.5, 1.0, 1.1, 2.1, 5.6],
    medium: [13, 3.0, 1.3, 0.7, 0.4, 0.7, 1.3, 3.0, 13],
    high: [29, 4.0, 1.5, 0.3, 0.2, 0.3, 1.5, 4.0, 29],
  },
  // ... other row configurations
};

// Get multiplier for a given position
function getMultiplier(
  rows: number,
  risk: 'low' | 'medium' | 'high',
  position: number
): number {
  return MULTIPLIERS[rows][risk][position];
}

// Calculate position from path
function getPositionFromPath(path: ('L' | 'R')[]): number {
  return path.filter((d) => d === 'R').length;
}

// Place a Plinko bet
async function placeBlinkoBet(params: PlinkoBetParams): Promise<PlinkoBetResult> {
  const clientSeed = params.clientSeed || generateClientSeed();

  const response = await fetch(\`\${API_BASE}/games/plinko/bet\`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionToken: params.sessionToken,
      amount: params.amount,
      rows: params.rows,
      risk: params.risk,
      clientSeed,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Bet placement failed');
  }

  const result = await response.json();
  return result.data;
}

// Generate a random client seed
function generateClientSeed(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let seed = '';
  for (let i = 0; i < 16; i++) {
    seed += chars[Math.floor(Math.random() * chars.length)];
  }
  return seed;
}

// Animate the ball path (for UI integration)
async function animatePath(
  path: ('L' | 'R')[],
  onBounce: (index: number, direction: 'L' | 'R', position: number) => void,
  delayMs: number = 150
): Promise<void> {
  let position = 0;

  for (let i = 0; i < path.length; i++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));

    if (path[i] === 'R') {
      position++;
    }

    onBounce(i, path[i], position);
  }
}

// Example usage
async function main() {
  const sessionToken = 'your-session-token';

  try {
    const result = await placeBlinkoBet({
      sessionToken,
      amount: 5.00,
      rows: 12,
      risk: 'medium',
    });

    console.log('Bet placed successfully!');
    console.log(\`Bet ID: \${result.betId}\`);

    // Animate the ball path
    await animatePath(result.path, (index, direction, position) => {
      console.log(\`Bounce \${index + 1}: \${direction} -> Position \${position}\`);
    });

    console.log(\`Final Position: \${result.position}\`);
    console.log(\`Multiplier: \${result.multiplier}x\`);
    console.log(\`Payout: \${result.payout}\`);
  } catch (error) {
    console.error('Bet failed:', error);
  }
}

main();`}
			</CodeBlock>

			<h2>Provably Fair Verification</h2>
			<p>
				Every Plinko game result can be independently verified. The path through
				the board is determined by the combined seeds and nonce.
			</p>

			<Step number={1} title="Receive Server Seed Hash">
				<p>
					Before placing bets, you receive a <code>serverSeedHash</code> which
					commits the server to a specific seed without revealing it.
				</p>
			</Step>

			<Step number={2} title="Provide Client Seed">
				<p>
					Supply your own <code>clientSeed</code> to ensure you have input into
					the random generation.
				</p>
			</Step>

			<Step number={3} title="Path Generation">
				<p>
					The server generates the ball path using HMAC-SHA256 with the combined
					seeds. Each bounce direction is determined by one byte of the hash.
				</p>
			</Step>

			<Step number={4} title="Verify After Seed Rotation">
				<p>
					When you rotate your server seed, verify the revealed seed matches the
					hash and recalculate the path to confirm results.
				</p>
			</Step>

			<CodeBlock language="typescript" title="Verification Example">
				{`import { createHash, createHmac } from 'crypto';

function verifyPlinkoResult(
  serverSeed: string,
  serverSeedHash: string,
  clientSeed: string,
  nonce: number,
  rows: number,
  expectedPath: ('L' | 'R')[],
  expectedPosition: number
): boolean {
  // Step 1: Verify server seed hash
  const computedHash = createHash('sha256')
    .update(serverSeed)
    .digest('hex');

  if (computedHash !== serverSeedHash) {
    console.error('Server seed hash mismatch!');
    return false;
  }

  // Step 2: Generate the path
  const hmac = createHmac('sha256', serverSeed)
    .update(\`\${clientSeed}:\${nonce}\`)
    .digest('hex');

  const calculatedPath: ('L' | 'R')[] = [];
  let position = 0;

  for (let i = 0; i < rows; i++) {
    const byteIndex = i * 2;
    const byte = parseInt(hmac.substring(byteIndex, byteIndex + 2), 16);

    if (byte % 2 === 1) {
      calculatedPath.push('R');
      position++;
    } else {
      calculatedPath.push('L');
    }
  }

  // Step 3: Compare path and position
  const pathMatches = calculatedPath.every(
    (dir, i) => dir === expectedPath[i]
  );

  if (!pathMatches) {
    console.error('Path mismatch!');
    return false;
  }

  if (position !== expectedPosition) {
    console.error(\`Position mismatch! Expected \${expectedPosition}, got \${position}\`);
    return false;
  }

  console.log('Verification successful!');
  return true;
}`}
			</CodeBlock>

			<h2>UI Animation Tips</h2>
			<p>
				When building a Plinko UI, consider these tips for smooth animations:
			</p>

			<ul>
				<li>
					<strong>Stagger bounces:</strong> Add 100-200ms delay between each peg
					for realistic physics
				</li>
				<li>
					<strong>Ease animations:</strong> Use easeOut for natural ball
					deceleration
				</li>
				<li>
					<strong>Sound effects:</strong> Play peg bounce sounds for each
					direction change
				</li>
				<li>
					<strong>Highlight path:</strong> Light up pegs as the ball passes for
					visual clarity
				</li>
				<li>
					<strong>Result anticipation:</strong> Slow down final bounces for
					dramatic effect
				</li>
			</ul>

			<h2>Error Handling</h2>
			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'message', header: 'Message' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{
						code: 'INVALID_ROWS',
						message: 'Rows must be 8, 10, 12, 14, or 16',
						solution: 'Use one of the valid row counts',
					},
					{
						code: 'INVALID_RISK',
						message: 'Risk must be "low", "medium", or "high"',
						solution: 'Use exactly one of the valid risk levels',
					},
					{
						code: 'INSUFFICIENT_BALANCE',
						message: 'Insufficient balance for bet',
						solution: 'Check player balance before placing bet',
					},
					{
						code: 'BET_BELOW_MINIMUM',
						message: 'Bet amount below minimum',
						solution: 'Increase bet to meet minimum requirement',
					},
					{
						code: 'BET_ABOVE_MAXIMUM',
						message: 'Bet amount exceeds maximum',
						solution: 'Reduce bet to stay within limits',
					},
				]}
			/>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/provably-fair">
							Learn more about Provably Fair verification
						</a>
					</li>
					<li>
						<a href="/docs/games-dice">Explore the Dice game</a>
					</li>
					<li>
						<a href="/docs/games-coinflip">Try the simple Coin Flip game</a>
					</li>
				</ul>
			</div>
		</div>
	);
}
